import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout.web";
import { MotiView } from "moti";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../components/common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import {
  useGetTenantForSchoolAdminQuery,
  useUpdateTenantMutation,
  useUploadFaviconMutation,
  useParseAndSaveSamlMetadataMutation,
  StoragePathType,
} from "../../graphql/generated/graphql";

import useResponsiveScreen from "../../hooks/useResponsiveScreen";
import { Books } from "@phosphor-icons/react/dist/csr/Books";
import { Palette } from "@phosphor-icons/react/dist/csr/Palette";
import { Calculator } from "@phosphor-icons/react/dist/csr/Calculator";
import { Question } from "@phosphor-icons/react/dist/csr/Question";
import { FileText } from "@phosphor-icons/react/dist/csr/FileText";
import { ArrowCounterClockwise } from "@phosphor-icons/react/dist/csr/ArrowCounterClockwise";
import { Check } from "@phosphor-icons/react/dist/csr/Check";
import { Image as ImageIcon } from "@phosphor-icons/react/dist/csr/Image";
import NewCustomAxioActionModal from "../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import ImageUpload from "../../components/common/general/ImageUpload/ImageUpload";

function AdminSettingsView() {
  const { isLarge } = useResponsiveScreen();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSamlMetadataModal, setShowSamlMetadataModal] = useState(false);
  const [samlMetadataUrl, setSamlMetadataUrl] = useState("");

  const [buttonLabel, setButtonLabel] = useState("Fetch & Save");
  const [parsedMetadata, setParsedMetadata] = useState<{
    entityId: string;
    ssoUrl: string;
    cert: string;
    expiry: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    logo: "",
    favicon: "",
    primaryColor: "",
    financialInformationUrl: "",
    helpCenterUrl: "",
    schoolResourcesUrl: "",
    officialTranscriptsUrl: "",
  });

  const [originalData, setOriginalData] = useState(formData);

  const {
    data: tenantData,
    loading: getTenantLoading,
    error: getTenantError,
    refetch: refetchTenant,
  } = useGetTenantForSchoolAdminQuery();

  const [updateTenant] = useUpdateTenantMutation();
  const [uploadFavicon] = useUploadFaviconMutation();

  const tenant = tenantData?.getTenantForSchoolAdmin?.data?.[0];

  const [parseAndSaveSamlMetadata] = useParseAndSaveSamlMetadataMutation();

  const handleSamlMetadataSubmit = async () => {
    if (!samlMetadataUrl) return setErrorMessage("Please enter a metadata URL");

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const { data } = await parseAndSaveSamlMetadata({
        variables: { metadataUrl: samlMetadataUrl },
      });

      const response = data?.parseAndSaveSamlMetadata;

      if (!response?.success) throw new Error(response?.message);
      setParsedMetadata({
        entityId: response.data.issuer,
        ssoUrl: response.data.entryPoint,
        cert: response.data.cert,
        expiry: response.data.expiry || "",
      });
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to import metadata");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoAfterUpload = (logoUrl: string) => {
    updateFormData("logo", logoUrl);
    refetchTenant();
  };

  const handleFaviconUpload = async () => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/jpeg";

      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          await handleFaviconFileUpload(file);
        }
      };

      input.click();
    } catch (error) {
      setErrorMessage("Failed to open file picker");
      setIsSubmitting(false);
    }
  };

  const handleFaviconFileUpload = async (file: File) => {
    if (file.size > 1 * 1024 * 1024) {
      setErrorMessage("Favicon file size should not exceed 1MB");
      return;
    }

    const acceptedTypes = ["image/png", "image/x-icon", "image/vnd.microsoft.icon", "image/svg+xml", "image/jpeg"];
    if (!acceptedTypes.includes(file.type)) {
      setErrorMessage("Only PNG, ICO, SVG, and JPG files are allowed for favicon");
      return;
    }

    // Validate dimensions for raster files; skip SVG and ICO aliases
    if (file.type !== "image/svg+xml" && file.type !== "image/x-icon" && file.type !== "image/vnd.microsoft.icon") {
      const img = new Image();
      img.onload = async () => {
        if (img.width < 16 || img.width > 1024 || img.height < 16 || img.height > 1024) {
          setErrorMessage("Favicon dimensions must be between 16×16 and 1024×1024 pixels");
          return;
        }
        if (img.width !== img.height) {
          setErrorMessage("Favicon must be square (1:1 aspect ratio)");
          return;
        }
        await processFaviconUpload(file);
      };
      img.onerror = () => {
        setErrorMessage("Invalid image file");
      };
      img.src = URL.createObjectURL(file);
    } else if (file.type === "image/x-icon" || file.type === "image/vnd.microsoft.icon") {
      await uploadIcoAsPng(file);
    } else {
      // For SVG files, skip dimension validation
      await processFaviconUpload(file);
    }
  };

  const uploadIcoAsPng = async (file: File) => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const base64Data: string = await new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          try {
            const targetSize = 180;
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const side = Math.min(img.width, img.height) || targetSize;
            canvas.width = targetSize;
            canvas.height = targetSize;
            if (!ctx) {
              reject(new Error("Canvas is not supported"));
              URL.revokeObjectURL(objectUrl);
              return;
            }
            const sx = Math.max(0, (img.width - side) / 2);
            const sy = Math.max(0, (img.height - side) / 2);
            ctx.drawImage(img, sx, sy, side, side, 0, 0, targetSize, targetSize);
            const dataUrl = canvas.toDataURL("image/png");
            resolve(dataUrl.split(",")[1]);
          } catch (err) {
            reject(err);
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Invalid ICO image"));
        };
        img.src = objectUrl;
      });

      // approximate byte size of base64 payload
      const estimatedSize = Math.ceil((base64Data.length * 3) / 4);
      const result = await uploadFavicon({
        variables: {
          favicon: base64Data,
          contentType: "image/png",
          size: estimatedSize,
        },
      });

      if (result.data?.uploadFavicon?.success) {
        const faviconUrl = result.data.uploadFavicon.data;
        updateFormData("favicon", faviconUrl);
        refetchTenant();
      } else {
        throw new Error(result.data?.uploadFavicon?.message || "Failed to upload favicon");
      }
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to upload favicon");
    } finally {
      setIsSubmitting(false);
    }
  };

  const processFaviconUpload = async (file: File) => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        const base64Data = base64String.split(",")[1];

        const result = await uploadFavicon({
          variables: {
            favicon: base64Data,
            contentType: file.type,
            size: file.size,
          },
        });

        if (result.data?.uploadFavicon?.success) {
          const faviconUrl180 = result.data.uploadFavicon.data;
          updateFormData("favicon", faviconUrl180);
          refetchTenant();
        } else {
          throw new Error(result.data?.uploadFavicon?.message || "Failed to upload favicon");
        }
        setIsSubmitting(false);
      };
      reader.onerror = () => {
        setErrorMessage("Failed to read file");
        setIsSubmitting(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setErrorMessage(error.message || "Failed to upload favicon");
      setIsSubmitting(false);
    }
  };

  // Initialize form data when tenant data loads
  useEffect(() => {
    if (tenant) {
      const initialData = {
        logo: tenant.logo || "",
        favicon: tenant.favicon180Url || tenant.favicon16Url || "",
        primaryColor: tenant.primaryColor || "#434756",
        financialInformationUrl: tenant.financialInformationUrl || "",
        helpCenterUrl: tenant.helpCenterUrl || "",
        schoolResourcesUrl: tenant.schoolResourcesUrl || "",
        officialTranscriptsUrl: tenant.officialTranscriptsUrl || "",
      };
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [tenant]);

  // Check for changes
  useEffect(() => {
    const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasChanges(hasFormChanges);
  }, [formData, originalData]);

  const updateFormData = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrorMessage("");
  };

  const validateHexColor = (color: string): boolean => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  const handleColorChange = (color: string) => {
    if (!color.startsWith("#")) {
      color = "#" + color;
    }
    updateFormData("primaryColor", color);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage("");

      // Validate hex color
      if (!validateHexColor(formData.primaryColor)) {
        throw new Error("Please enter a valid hex color code (e.g., #434756)");
      }

      const result = await updateTenant({
        variables: {
          tenant: {
            primaryColor: formData.primaryColor,
            financialInformationUrl: formData.financialInformationUrl,
            helpCenterUrl: formData.helpCenterUrl,
            schoolResourcesUrl: formData.schoolResourcesUrl,
            officialTranscriptsUrl: formData.officialTranscriptsUrl,
            logo: formData.logo,
          },
        },
      });

      if (result.data?.updateTenant?.success) {
        setOriginalData(formData);
        setHasChanges(false);
        refetchTenant();
      } else {
        throw new Error(result.data?.updateTenant?.message || "Failed to save settings");
      }
    } catch (error) {
      setErrorMessage(error.message || "Failed to save settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
    setHasChanges(false);
    setErrorMessage("");
  };

  const getFaviconPreviewUrl = (): string => {
    const candidate =
      tenant?.favicon180Url ||
      tenant?.favicon16Url ||
      (formData.favicon ? `${formData.favicon}/favicon-180x180.png?alt=media` : "");
    if (!candidate) return "";
    const sep = candidate.includes("?") ? "&" : "?";
    return `${candidate}${sep}t=${Date.now()}`;
  };

  if (getTenantLoading) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </AdminLayout>
    );
  }

  if (getTenantError) {
    return (
      <AdminLayout withPadding={false}>
        <View style={styles.pageBackground}>
          <View style={styles.errorContainer}>
            <CustomText text="Failed to load settings" size="l" useTranslationText={false} />
          </View>
        </View>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}>
            <View>
              <CustomText
                text="LMS Settings"
                size="m"
                useTranslationText={false}
                textType="display"
                weight="ultraBold"
                role="heading"
                aria-level="1"
                style={{ color: "#101828" }}
              />

              <CustomSpacing type="vertical" size="s" />
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <CustomText
                  text="Customize your institution's LMS appearance and student experience."
                  size="m"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#667085" }}
                />
                <CustomButton
                  text="Upload SAML Metadata URL"
                  size="s"
                  styleType="primary"
                  onPress={() => setShowSamlMetadataModal(true)}
                />
              </View>
            </View>

            <CustomSpacing type="vertical" size="xl" />

            <View style={[styles.mainLayout, !isLarge && styles.mainLayoutMobile]}>
              <View style={[styles.leftColumn, !isLarge && styles.leftColumnMobile]}>
                <View style={!isLarge ? [styles.sectionCard, styles.mobileSectionCard] : styles.leftSectionCard}>
                  <View style={styles.sectionHeader}>
                    <View style={styles.sectionIconContainer}>
                      <View style={[styles.sectionIcon, { backgroundColor: "#F2D6E6" }]}>
                        <Palette size={24} color="#933368" />
                      </View>
                    </View>
                    <View style={styles.sectionHeaderText}>
                      <CustomText
                        text="Branding & Customization"
                        size="l"
                        weight="bold"
                        useTranslationText={false}
                        style={{ color: "#101828" }}
                      />
                      <CustomText
                        text="Customize your institution's visual identity and brand elements."
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={{ color: "#667085" }}
                      />
                    </View>
                  </View>

                  <CustomSpacing type="vertical" size="l" />

                  <View>
                    <CustomText
                      text="Institution Logo"
                      size="m"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#344054", marginBottom: 8 }}
                    />
                    <ImageUpload
                      value={formData.logo}
                      onChange={handleLogoAfterUpload}
                      storagePath={StoragePathType.TenantLogo}
                      imageStyle={{
                        width: "auto !important",
                        minHeight: 120,
                        maxHeight: 200,
                        objectFit: "contain",
                        borderRadius: 6,
                      }}
                      placeholderContent={<ImageIcon size={24} color="#9CA3AF" />}
                    />
                  </View>

                  <CustomSpacing type="vertical" size="xl" />

                  <View>
                    <CustomText
                      text="Color Theme"
                      size="m"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#344054", marginBottom: 16 }}
                    />

                    <View>
                      <CustomText
                        text="Primary Color (Buttons & Links)"
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={{ color: "#344054", marginBottom: 8 }}
                      />

                      <View style={styles.colorInputContainer}>
                        <View
                          style={[
                            styles.colorPreview,
                            {
                              backgroundColor: validateHexColor(formData.primaryColor)
                                ? formData.primaryColor
                                : "#D1D5DB",
                            },
                          ]}
                        />
                        <CustomTextInput
                          value={formData.primaryColor}
                          onChangeText={handleColorChange}
                          placeholder={tenant?.primaryColor || "#434756"}
                          style={styles.colorInput}
                        />
                      </View>

                      {!validateHexColor(formData.primaryColor) && formData.primaryColor && (
                        <CustomText
                          text="Please enter a valid hex color code"
                          size="xs"
                          useTranslationText={false}
                          style={{ color: "#EF4444", marginTop: 4 }}
                        />
                      )}
                    </View>

                    <CustomSpacing type="vertical" size="m" />

                    <View>
                      <CustomText
                        text="Preview"
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={{ color: "#344054", marginBottom: 8 }}
                      />
                      <View style={styles.previewContainer}>
                        <CustomButton
                          text="Sample Button"
                          styleType="primary"
                          size="m"
                          onPress={() => {}}
                          bold={true}
                          useTranslationText={false}
                          style={{
                            backgroundColor: validateHexColor(formData.primaryColor)
                              ? formData.primaryColor
                              : "#3E68FE",
                            maxWidth: "140px",
                          }}
                          textStyle={{ fontSize: 12 }}
                        />
                        <CustomText
                          text="Button text uses the selected text color on the primary background."
                          size="xs"
                          useTranslationText={false}
                          style={{ color: "#667085" }}
                        />
                      </View>
                    </View>
                  </View>

                  <CustomSpacing type="vertical" size="xl" />

                  <View>
                    <CustomText
                      text="Favicon"
                      size="m"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#344054", marginBottom: 8 }}
                    />

                    <div
                      style={{
                        width: "100%",
                        minHeight: 120,
                        maxHeight: 200,
                        borderRadius: 8,
                        borderWidth: 2,
                        borderStyle: "dashed",
                        borderColor: "#D1D5DB",
                        backgroundColor: "#F9FAFB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                        cursor: "pointer",
                        padding: 16,
                        boxSizing: "border-box",
                      }}
                      onClick={handleFaviconUpload}>
                      {formData.favicon || tenant?.favicon180Url ? (
                        <img
                          src={getFaviconPreviewUrl()}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: 6,
                          }}
                          alt="Favicon"
                        />
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            textAlign: "center",
                          }}>
                          <ImageIcon size={24} color="#9CA3AF" />
                          <CustomText
                            text={isSubmitting ? "Uploading..." : "Click to upload favicon"}
                            size="m"
                            style={{ color: "#344054", fontWeight: "500" }}
                            useTranslationText={false}
                          />
                          <CustomText
                            text="PNG, ICO, SVG, JPG up to 1MB"
                            size="s"
                            style={{ color: "#9CA3AF" }}
                            useTranslationText={false}
                          />
                          <CustomText
                            text="Square ratio (1:1) required, 16×16 to 1024×1024px"
                            size="xs"
                            style={{ color: "#9CA3AF" }}
                            useTranslationText={false}
                          />
                        </div>
                      )}
                    </div>
                  </View>
                </View>
              </View>

              <View style={[styles.rightColumn, !isLarge && styles.rightColumnMobile]}>
                <View
                  style={
                    !isLarge
                      ? [styles.sectionCard, styles.mobileSectionCard, styles.resourcesCard]
                      : styles.rightSectionCard
                  }>
                  <View style={styles.sectionHeader}>
                    <View style={styles.sectionIconContainer}>
                      <View style={[styles.sectionIcon, { backgroundColor: "#D1F2F6" }]}>
                        <Books size={24} color="#157984" />
                      </View>
                    </View>
                    <View style={styles.sectionHeaderText}>
                      <CustomText
                        text="Student Resources Panel"
                        size="l"
                        weight="bold"
                        useTranslationText={false}
                        style={{ color: "#101828" }}
                      />
                      <CustomText
                        text="Manage the resources shown in the student slide-out panel."
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={{ color: "#667085" }}
                      />
                    </View>
                  </View>

                  <CustomSpacing type="vertical" size="l" />

                  <View style={styles.resourceFields}>
                    <View style={styles.resourceField}>
                      <View style={styles.resourceIconText}>
                        <View style={styles.resourceIcon}>
                          <Calculator size={20} color="#16A34A" />
                        </View>
                        <CustomText
                          text="Financial Information"
                          size="s"
                          weight="medium"
                          useTranslationText={false}
                          style={{ color: "#101828" }}
                        />
                      </View>
                      <CustomTextInput
                        value={formData.financialInformationUrl}
                        onChangeText={(value) => updateFormData("financialInformationUrl", value)}
                        placeholder="https://university.edu/financial-aid"
                        style={styles.urlInput}
                      />
                      <CustomText
                        text="Link to tuition, payments, and financial aid information"
                        size="xs"
                        useTranslationText={false}
                        style={{ color: "#667085" }}
                      />
                    </View>

                    <View style={styles.resourceField}>
                      <View style={styles.resourceIconText}>
                        <View style={styles.resourceIcon}>
                          <Question size={20} color="#101828" />
                        </View>
                        <CustomText
                          text="Help Center"
                          size="s"
                          weight="medium"
                          useTranslationText={false}
                          style={{ color: "#101828" }}
                        />
                      </View>
                      <CustomTextInput
                        value={formData.helpCenterUrl}
                        onChangeText={(value) => updateFormData("helpCenterUrl", value)}
                        placeholder="https://help.university.edu"
                        style={styles.urlInput}
                      />
                      <CustomText
                        text="FAQ and documentation for students"
                        size="xs"
                        useTranslationText={false}
                        style={{ color: "#667085" }}
                      />
                    </View>

                    <View style={styles.resourceField}>
                      <View style={styles.resourceIconText}>
                        <View style={styles.resourceIcon}>
                          <Books size={20} color="#7B3395" />
                        </View>
                        <CustomText
                          text="School Resources"
                          size="s"
                          weight="medium"
                          useTranslationText={false}
                          style={{ color: "#101828" }}
                        />
                      </View>
                      <CustomTextInput
                        value={formData.schoolResourcesUrl}
                        onChangeText={(value) => updateFormData("schoolResourcesUrl", value)}
                        placeholder="https://resources.university.edu"
                        style={styles.urlInput}
                      />
                      <CustomText
                        text="Library, downloads, and institutional resources"
                        size="xs"
                        useTranslationText={false}
                        style={{ color: "#667085" }}
                      />
                    </View>

                    <View style={styles.resourceField}>
                      <View style={styles.resourceIconText}>
                        <View style={styles.resourceIcon}>
                          <FileText size={20} color="#101828" />
                        </View>
                        <CustomText
                          text="Official Transcripts"
                          size="s"
                          weight="medium"
                          useTranslationText={false}
                          style={{ color: "#101828" }}
                        />
                      </View>
                      <CustomTextInput
                        value={formData.officialTranscriptsUrl}
                        onChangeText={(value) => updateFormData("officialTranscriptsUrl", value)}
                        placeholder="https://transcripts.university.edu"
                        style={styles.urlInput}
                      />
                      <CustomText
                        text="Request or download official transcripts"
                        size="xs"
                        useTranslationText={false}
                        style={{ color: "#667085" }}
                      />
                    </View>
                  </View>
                </View>
              </View>
            </View>

            {errorMessage && (
              <View style={styles.errorMessageContainer}>
                <CustomText text={errorMessage} size="s" useTranslationText={false} style={{ color: "#EF4444" }} />
              </View>
            )}

            <CustomSpacing type="vertical" size="xl" />

            <View style={styles.footerActions}>
              <View style={styles.lastSaved}>
                <CustomText
                  text="Last saved: Today at 2:30 PM"
                  size="s"
                  useTranslationText={false}
                  style={{ color: "#667085" }}
                />
              </View>

              <View style={styles.actionButtons}>
                <CustomButton
                  text="Reset Changes"
                  styleType="primaryLight"
                  size="m"
                  bold={true}
                  onPress={handleReset}
                  disabled={!hasChanges || isSubmitting}
                  useTranslationText={false}
                  style={(!hasChanges || isSubmitting) && styles.disabledButton}
                  textStyle={{ color: "#3E68FE" }}
                  leftIcon={<ArrowCounterClockwise size={24} color="#3E68FE" />}
                />
                <CustomButton
                  text={isSubmitting ? "Saving..." : "Save Settings"}
                  styleType="primary"
                  size="m"
                  bold={true}
                  onPress={handleSave}
                  disabled={!hasChanges || isSubmitting}
                  useTranslationText={false}
                  style={(!hasChanges || isSubmitting) && styles.disabledButton}
                  leftIcon={<Check size={24} color="#FFFFFF" />}
                />
              </View>
            </View>
          </MotiView>
        </View>
      </View>
      <NewCustomAxioActionModal
        visible={showSamlMetadataModal}
        onClose={() => {
          setShowSamlMetadataModal(false);
          setSamlMetadataUrl("");
          setParsedMetadata(null);
          setErrorMessage("");
          setButtonLabel("Save");
        }}
        onAction={async () => {
          await handleSamlMetadataSubmit();

          if (parsedMetadata) {
            setButtonLabel("Saved");
            setTimeout(() => setButtonLabel("Fetch & Save"), 3000);
          }
        }}
        modalTexts={{
          title: "Import SAML Metadata",
          ariaLabel: "saml-metadata-upload",
          warningText: "Paste your Identity Provider’s metadata URL below.",
          actionButtonText: parsedMetadata ? "Saved" : "Fetch & Save",
        }}>
        <CustomTextInput
          value={samlMetadataUrl}
          onChangeText={setSamlMetadataUrl}
          placeholder="https://idp.example.com/metadata.xml"
          style={{ marginBottom: 24 }}
        />
        {parsedMetadata && (
          <View style={{ marginTop: 16 }}>
            <CustomText useTranslationText={false} text="Metadata Parsed" weight="bold" style={{ marginBottom: 12 }} />

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText text="Entity ID: " size="s" weight="medium" useTranslationText={false} />
              <CustomText useTranslationText={false} text={parsedMetadata.entityId} size="s" />
            </View>

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText text="SSO URL: " size="s" weight="medium" useTranslationText={false} />
              <CustomText useTranslationText={false} text={parsedMetadata.ssoUrl} size="s" />
            </View>

            <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
              <CustomText useTranslationText={false} text="Cert Expires: " size="s" weight="medium" />

              <CustomText
                useTranslationText={false}
                text={parsedMetadata.expiry}
                size="xs"
                style={{
                  color: "#6B7280",
                  marginTop: 2,
                }}
              />
            </View>

            <View style={{ marginBottom: 12 }}>
              <CustomText useTranslationText={false} text="Certificate: " size="s" weight="medium" />
              <CustomText
                useTranslationText={false}
                text={parsedMetadata.cert}
                size="xs"
                style={{ color: "#6B7280", marginTop: 2 }}
              />
            </View>
          </View>
        )}

        {errorMessage && (
          <CustomText text={errorMessage} size="s" style={{ color: "red", marginTop: 12, marginBottom: 12 }} />
        )}
      </NewCustomAxioActionModal>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  mainLayout: {
    flexDirection: "row",
    gap: 0,
    alignItems: "stretch",
  },
  mainLayoutMobile: {
    flexDirection: "column",
    gap: 24,
  },
  leftColumn: {
    flex: 2,
  },
  leftColumnMobile: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
  },
  rightColumnMobile: {
    flex: 1,
  },
  sectionCard: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
    height: "100%",
    padding: 24,
  },
  leftSectionCard: {
    backgroundColor: "#FFFFFF",
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderWidth: 1,
    borderRightWidth: 0,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
    height: "100%",
    padding: 24,
  },
  rightSectionCard: {
    backgroundColor: "#FCFCFD",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
    height: "100%",
    padding: 24,
  },
  mobileSectionCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    shadowOpacity: 0.1,
    padding: 24,
  },
  resourcesCard: {
    backgroundColor: "#FCFCFD",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  sectionIconContainer: {
    marginTop: 4,
  },
  sectionIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  sectionHeaderText: {
    flex: 1,
    gap: 4,
  },
  colorInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  colorPreview: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  colorInput: {
    flex: 1,
    maxWidth: 200,
  },
  previewContainer: {
    backgroundColor: "#F9FAFB",
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    gap: 8,
  },
  resourceFields: {
    gap: 24,
  },
  resourceField: {
    gap: 8,
  },
  resourceIconText: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  resourceIcon: {
    width: 24,
    height: 24,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
  },
  urlInput: {
    width: "100%",
  },
  errorMessageContainer: {
    backgroundColor: "#FEF2F2",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FECACA",
    marginTop: 16,
  },
  footerActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  lastSaved: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 12,
  },
  disabledButton: {
    opacity: 0.5,
  },
});

export default AdminSettingsView;
